import type {
  MovementPattern,
  RoundType,
  TodayProposalView,
  WorkoutEntryGroup,
} from '@heraion/shared';
import type { DraftSet } from './draftSetTypes';

// The set's shapes live in draftSetTypes.ts (#336, the 500-line cap);
// re-exported here so the existing importers keep their path.
export type { DraftSet, SetState, WeightUnit } from './draftSetTypes';
export { setStateOf } from './draftSetTypes';

// Draft state for the mobile Logger. Mirrors the web's WorkoutLog
// draft surface, trimmed to what mobile can capture today:
//   - weighted + bodyweight set kinds (reps, weight or extra, per-set
//     unit kg/lb, tri-state pill: pending/done/failed)
//   - per-set note (500 chars) and RPE (1..10)
//   - superset / circuit grouping via WorkoutEntryGroup on each entry
//   - edit + repeat hydration (LoggerScreen.hydrateFromWorkout) — saves
//     can overwrite an existing session or clone its structure
//
// Still NOT modelled here (handled elsewhere or out-of-scope for the
// mobile logger surface): p1rm/loaded-carry/duration/side set kinds,
// dropsets, exercise-level notes/RPE.


/** The identity of a draft ROW, for everything that has to say WHICH ROW:
 *  the logger cursor, the roster highlight, React keys, findIndex over
 *  entries (LG-39).
 *
 *  ⛔ Do not inline `entry.exerciseId` for this — that is the catalog FK, it
 *  is not unique, and using it here is the bug this helper exists to keep
 *  from coming back. Questions about WHICH EXERCISE (history, PR scope, rest
 *  defaults, display name) keep reading exerciseId directly.
 *
 *  The fallback covers plain DraftEntry values with no flat store behind them
 *  (test fixtures, a draft mid-hydration): they behave exactly as before. */
export const rowIdOf = (e: DraftEntry): string => e.entryId ?? e.exerciseId;

/** The inverse of `rowIdOf`: which position that row occupies RIGHT NOW.
 *  `-1` when it is gone, exactly like the findIndex it replaces.
 *
 *  ⛔ It exists so that nothing keeps an INDEX across a dispatch (LG-43).
 *  An index is only true for the render that produced it: `move_group_member`
 *  swaps entries and every stored position silently means another row. Keep
 *  the id, ask for the position at the moment you need it. */
export const resolveRowIndex = (
  entries: readonly DraftEntry[],
  rowId: string,
): number => entries.findIndex((e) => rowIdOf(e) === rowId);

/** The row the cursor points at, or `undefined` when it points at nothing —
 *  a null cursor (Option B: no auto-focus) and a row that has since been
 *  removed are the same answer, and both callers want the same guard.
 *
 *  It is `resolveRowIndex` plus that guard, in one place: the header asks it
 *  twice per render (is the active row inside a superset · which exercise a
 *  manual rest belongs to), and two hand-rolled `find`s over the same cursor
 *  are exactly what drifts. */
export const activeEntryOf = (
  entries: readonly DraftEntry[],
  rowId: string | null,
): DraftEntry | undefined => {
  if (rowId === null) return undefined;
  const i = resolveRowIndex(entries, rowId);
  return i >= 0 ? entries[i] : undefined;
};


export interface DraftEntry {
  /** Stable identity of THIS row (LG-39). The flat store has minted one per
   *  entry since Phase 1 (ExerciseMeta.entryId) — this field only exposes it
   *  to the nested view the UI reads, via assembleDraftKeepIds.
   *
   *  ⛔ It exists because `exerciseId` is NOT unique: the same exercise can
   *  legitimately appear two or three times in one workout (walk to warm up,
   *  HIIT walking, walk to cool down — Antenore 2026-08-14). Anything that
   *  answers "WHICH ROW" — the cursor, the roster highlight, a React key,
   *  a findIndex over entries — keys on entryId. Anything that answers
   *  "WHICH EXERCISE" — history lookups, PR scope, rest defaults, catalog
   *  name resolution — keys on exerciseId and MUST keep doing so.
   *
   *  ⛔ Duplicate rows are NOT variants: three walks are three rows with no
   *  equipment variant, and their sets belong to the SAME historical series.
   *  Deducing a variant from a duplicate splits that series into fakes and
   *  nothing in the app shows it (equipment-model.md §10.2, §10.5).
   *
   *  Optional like every other late-added draft field: the many DraftEntry
   *  constructors (hydration, plan starter, test fixtures) build plain
   *  entries and flattenDraft mints the id for them. On the one runtime path
   *  the UI actually reads (useLoggerDraft → assembleDraftKeepIds) it is
   *  always present. */
  entryId?: string;
  /** Catalog slug — Exercise.slug (also stored as Exercise.id). NOT an
   *  identity: see entryId above. */
  exerciseId: string;
  /** Snapshot for display while the Logger is open; the API stamps
   *  the canonical name from the catalog at write time. */
  exerciseName: string;
  /** Catalog snapshot of the exercise's bodyweight flag. Drives the
   *  payload shape (weighted vs bodyweight) and the column header
   *  (KG vs EXTRA). Kept on the entry, not the set, since a single
   *  exercise picked into the workout always logs one way. */
  isBodyweight: boolean;
  /** Catalog snapshot of the exercise's movement pattern. Read by the
   *  post-save MAX-bump evaluation (rep-trust cap differs per pattern,
   *  e.g. squat tolerates higher reps than overhead press). Null when
   *  the catalog row has no pattern set or the entry was hydrated from
   *  a saved workout (edit mode) without a fresh catalog lookup; the
   *  MAX-bump path falls back to the default rep-trust cap then. */
  movementPattern: MovementPattern | null;
  /** Superset/circuit membership. Null = standalone entry. Two or more
   *  entries with the same `group.id` (and same `kind`) form a group;
   *  validation runs at buildPayload time so a half-set group never
   *  reaches the API. */
  group: WorkoutEntryGroup | null;
  /** Default bilateral mode for new sets in this entry (1.45.0). The
   *  entry-header chip (eligibility-gated by equipment kind) flips
   *  this; subsequent add_set actions stamp the value onto the new
   *  DraftSet. Already-completed sets keep their stamped value so a
   *  mid-entry flip doesn't rewrite history. Falls back to 2 (the
   *  SL-style default) for legacy hydrations. */
  loadedSides: 1 | 2;
  /** Cardio entry flag (1.66.9). True when the underlying catalog row
   *  is `movementPattern === 'cardio'`. Drives the SetRow variant
   *  (CardioSetRow vs the weighted SetRow), buildPayload branch, and
   *  the WorkoutSet kind emitted at save. Stamped at add_exercise
   *  time and at hydrateFromWorkout time so the entry knows its own
   *  kind regardless of the catalog snapshot in use. */
  isCardio: boolean;
  /** Time-based (isometric) entry flag — plank, wall sit, bird-dog hold,
   *  the physiotherapy isometrics (inbox 3dd20b1c). True when the catalog
   *  row is `isTimeBased`. Like isCardio it drives a duration input instead
   *  of reps/weight and emits kind 'timed' at save, but with no
   *  distance/HR/calories. Stamped at add_exercise + hydrateFromWorkout. */
  isTimeBased: boolean;
  /** Per-session RPE column toggle (Logger redesign slice 3, §6.3).
   *  Seeded from PlannedExerciseEntry.rpeEnabled (or
   *  Slot.rpeEnabled propagated by programResolution) via
   *  resolveRpeDefault on plan-start; otherwise false. Per-set RPE
   *  values live on DraftSet.rpe and aren't deleted when the column
   *  is hidden — re-toggling shows them back. Not persisted to the
   *  server (it's a session-level chrome flag); slice 3 keeps it
   *  on the draft so resume / repeat both see the user's last
   *  per-session choice. */
  /** Per-session visibility of the single effort column (effort chip).
   *  ON by default for weighted entries (opt-out per entry, decided
   *  2026-05-26); off for bodyweight/cardio. The scale (RIR | RPE) is
   *  the app-wide Profile.effortScale, not a per-entry choice. Per-set
   *  values live on DraftSet.rpe / DraftSet.rir and aren't deleted when
   *  the column is hidden. Not persisted to the server (session chrome);
   *  kept on the draft so resume / repeat see the user's last choice. */
  effortColumn: boolean;
  /** Per-session history-panel visibility (Logger redesign slice 4.1,
   *  §6 chip row §10.4). Hidden by default — the 🕐 chip toggles it
   *  open/closed. Per-session only; never persisted. */
  historyOpen: boolean;
  /** EQ-1 — WHICH physical machine this entry was logged on, as the bare
   *  slug (`machine-a`); the `equipment-instance:` namespace is applied at
   *  the write seam. Set at add (silently, only when the place knows
   *  exactly one) or at replace (the one place the athlete is ever asked).
   *  Null on every legacy hydration, which is what the whole history looks
   *  like — absence is the normal case, not a missing value. */
  equipmentInstance: string | null;
  /** EQ-1 — WHERE it was logged. The second axis: exercise x place is the
   *  list of special machines that place has. Sticky across entries; null
   *  until the athlete picks one. */
  location: string | null;
  /** The saved entry's catalog-tag snapshot, carried back so a correction
   *  does not erase it (#38). Edit only; absent everywhere else. */
  tags?: string[];
  /** PE-37 — PRESCRIPTION: the coach's per-EXERCISE note (`SlotDraft
   *  .instructions`), the twin of `DraftSet.notePrescribed` one level up.
   *  ⛔ Not the retired `DraftEntry.notes`: that was an authoring field the
   *  timeline logger removed, and reusing its name for a read-only
   *  prescription would make two different facts answer to one word.
   *  Absent = the exercise carries no note. */
  notePrescribed?: string;
  sets: DraftSet[];
}

// A standalone timeline note event (next/timeline-logger.md §9, Solution C).
// The single chat-style composer appends one of these; it renders as a note
// mini-card interleaved in the timeline feed by performedAt. Distinct from the
// per-set DraftSet.note and the legacy DraftEntry.notes (both kept for read-
// compat, authoring retired in the timeline logger). Persisted at workout save
// as its own WorkoutNote DDB row — never on the set-aggregation path.
export interface NoteEventDraft {
  /** Client UUID, also the server id (offline-first idempotency). */
  id: string;
  /** Free text (4..2000, trimmed). The composer enforces the floor too. */
  text: string;
  /** ISO when sent. Live logging: wall clock. Past-session edit: an
   *  in-window tail time (timelinePlacement.placeCompletedSetAtTail), never
   *  the live clock. */
  performedAt: string;
  /** Catalog slug in focus when the note was sent, if any. Data-only — no UI
   *  surface; carried for a future feed ("note while on Back Squat"). */
  exerciseContextId?: string;
}

export interface LoggerDraft {
  /** ISO. Set on first mount. */
  startedAt: string;
  /** ISO. User-typed end time via the footer date editor (1.67.1).
   *  Absent during an active new/repeat workout — buildPayload then
   *  falls back to the latest per-set completedAt. Hydrated from
   *  `w.endedAt` in edit mode so the user sees the stored value and
   *  can correct it. */
  endedAt?: string;
  entries: DraftEntry[];
  /** Plan row this draft was started from. Carried into the save
   *  payload (NewWorkout.fromPlanId) so the server can flip the plan
   *  row to status=completed on success. Optional — direct/manual
   *  sessions don't have one. */
  fromPlanId?: string;
  /** Plan-named title forwarded into Workout.name on save. Lets the
   *  saved session keep the plan's identity in the history list. */
  name?: string;
  /** Plan-level notes forwarded into Workout.notes on save. */
  notes?: string;
  /** Read-only coach context for the workout-level "?" panel (program
   *  description + the day's session note). Resolved at Start from the
   *  program/plan template; display-only, NEVER written to the save payload
   *  (no denormalized snapshot — see [[feedback_no_denormalized_display_snapshots]]).
   *  Absent for ad-hoc/manual/edit sessions. */
  coachNotes?: { programDescription?: string; sessionNote?: string };
  /** Display-only plan/program reference for resolving the coach-notes "?"
   *  panel when the draft was NOT freshly started from a plan — i.e. a
   *  past session opened in Edit or Repeat mode (hydrateFromWorkout copies
   *  the saved Workout.fromPlanId here). useCoachNotes resolves notes from
   *  the cached programs/plans against it. Kept SEPARATE from `fromPlanId`
   *  on purpose: `fromPlanId` drives the save (plan-flip / program
   *  auto-advance), and a Repeat must not re-flip the source plan — so the
   *  reference used for display never reaches the payload. */
  coachNotesRef?: string;
  /** AP-9b: the reasons of a built «today» draft, display-only like
   *  `coachNotes`. Present until the athlete presses Start. */
  todayProposal?: TodayProposalView;
  /** #336 S2: the program round's type when it is not normal, from the
   *  starter. Display-only like `coachNotes`, never in the save payload. */
  roundType?: Exclude<RoundType, 'normal'>;
  /** EQ-1 slice D — WHERE this session is happening, as the bare slug.
   *  Session-level because the place is a fact of the session: you pick it
   *  once, on arrival, and every entry created afterwards inherits it onto
   *  its own `location` (which is what actually reaches the set).
   *
   *  ⚠️ NOT a denormalised copy of the entries' value. This is the CURRENT
   *  CHOICE, live and changeable; each entry's `location` is the recorded
   *  fact of where that entry happened, and once written it stays put. They
   *  differ the moment you change gym mid-session — which is `EQ-1-SWITCH`,
   *  and the reason that voice is not free.
   *
   *  Optional so the many draft constructors need no synchronised bump;
   *  absent reads as "not said", exactly like every session before today. */
  location?: string | null;
  /** Standalone timeline note events (Solution C). Optional so the many
   *  LoggerDraft constructors (hydrate, plan-start, repeat, legacy persisted
   *  drafts) don't need a synchronised bump — absent reads as []. Authored
   *  only in the timeline logger; the card logger never sets it. Persisted as
   *  WorkoutNote rows at save (useSaveWorkout flush), never on the set path. */
  noteEvents?: NoteEventDraft[];
}

// The reducer action union moved to loggerActions.ts (2026-06-03) to keep
// this file — the draft data shapes — under the 500-line budget. Re-exported
// here so existing `import { LoggerAction } from './loggerDraftTypes'`
// callsites keep working.
export type { LoggerAction } from './loggerActions';
